import { FC, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import type { AircraftOverviewRow } from "src/shared/api-types/certificate";

import NavigationBack from "src/components/Navigation";
import { captureHandledException } from "src/services/sentry";

import Button from "../../components/Button";
import UserTitle from "../../components/UserTitle";
import { useCrewMembersNavigation } from "../../hooks/useCrewMembersNavigation";
import UserNavigation from "../user-navigation";
import {
  buildCatalogRequirements,
  getIncompleteRoles,
  isProfileSetupIncomplete,
  CatalogRequirements,
} from "./profileSetup";
import Positions from "./positions";
import Operations from "./operations";
import Aircraft from "./aircraft";
import AllCourses from "./all-courses";
import Bundles from "./bundles";
import CourseHistory from "../course-history";
import CurrentUserCrewContext from "../../context/CurrentUserCrewContext";
import CertificateActions from "../../context/CertificateActions";
import {
  getAircraftOverview,
  ComplianceWithCoursesResponseHydrated as ComplianceWithCourses,
  getUserCompliance,
} from "../../api/compliance";
import { getRelationTree } from "../../api/courses";
import { getUserBoughtCourses, getAvailableProducts } from "../../api/payment";
import { getUserInfo } from "../../api/user.deprecated";
import useUser from "../../hooks/useUser";
import useCrew from "../../hooks/useCrew";

// crewId/userId are absent on the self route (/user/compliance/:pageId); the
// component falls back to the current user/crew for them.
interface MatchParams {
  crewId?: string;
  userId?: string;
  pageId: string;
}

interface UserComplianceProps extends RouteComponentProps<MatchParams> {
  userId?: string;
  crewId?: string;
  user?: any;
  showBackButton?: boolean;
}

const UserCompliance: FC<UserComplianceProps> = ({
  match,
  history,
  location,
  userId: propUserId,
  crewId: propCrewId,
  user: propUser,
  showBackButton = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useUser();
  const { crew: currentCrew } = useCrew();

  const headerCaptions: Record<string, string> = {
    positions: t("compliance.positions"),
    operations: t("compliance.operations"),
    aircraft: t("compliance.aircraft"),
    all: t("compliance.all_courses"),
    bundles: t("compliance.bundles"),
  };

  const [historyItem, setHistoryItem] = useState<any>(null);
  const [complianceData, setComplianceData] = useState<ComplianceWithCourses | null>(null);
  // Crew catalog requirements for per-role profile-setup detection; null =
  // catalog unavailable → detection falls back to the legacy heuristic.
  const [catalogRequirements, setCatalogRequirements] = useState<CatalogRequirements | null>(null);
  const [aircraftOverviewRows, setAircraftOverviewRows] = useState<AircraftOverviewRow[]>([]);
  const [userInfo, setUserInfo] = useState<any>(propUser || null);
  const [boughtProducts, setBoughtProducts] = useState<any>(null);
  const [moodleProducts, setMoodleProducts] = useState<any>(null); // For All Courses tab
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMoodleCourses, setLoadingMoodleCourses] = useState(false); // For All Courses tab

  // Use props first, then URL params, then fall back to current user/crew
  const userIdStr = propUserId || match.params?.userId || currentUser?.id?.toString();
  const crewIdStr = propCrewId || match.params?.crewId || currentCrew?.id?.toString();

  // The self view is exactly the /user/compliance/:pageId route: no userId prop
  // and none in the URL (the manager route always carries one). A manager
  // opening their own record via the member list keeps the manager UI.
  const isSelfView = !propUserId && !match.params?.userId;

  const userId = Number(userIdStr);
  const crewId = Number(crewIdStr);

  const { handleGoBack } = useCrewMembersNavigation();

  const goToUserProperties = () => history.push(`/crew/${crewId}/members/${userId}/edit/properties`);

  // Fresh compliance calculation also refreshes the user aircraft-overview cache.
  const handleComplianceRefresh = async () => {
    const complianceResponseFresh = await getUserCompliance(crewId, userId, false);
    const aircraftOverviewFresh = await getAircraftOverview(crewId, currentCrew?.baseIds, userId);
    setComplianceData(complianceResponseFresh.data);
    setAircraftOverviewRows(aircraftOverviewFresh.data.rows);
  };

  useEffect(() => {
    (async () => {
      if (!userId || !crewId || isNaN(userId) || isNaN(crewId)) {
        setError(t("compliance.invalid_user_crew_id"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // If we don't have user info and we have a userId, fetch it
        let currentUserInfo = userInfo;
        if (!currentUserInfo && userId) {
          currentUserInfo = await getUserInfo(userId);
          setUserInfo(currentUserInfo);
        }

        // Step 1: Load cached data first (fast, shows cached data)
        const [complianceResponseCached, aircraftOverviewCached, userBoughtCourses, catalog] = await Promise.all([
          getUserCompliance(crewId, userId, true),
          getAircraftOverview(crewId, currentCrew?.baseIds, userId),
          getUserBoughtCourses(userId, crewId),
          // Best-effort: without the catalog the profile-setup detection falls
          // back to the legacy heuristic instead of blocking the page.
          getRelationTree(crewId)
            .then((response) => buildCatalogRequirements(response.tree))
            .catch((err) => {
              captureHandledException(err, { feature: "userCompliance", action: "loadCatalog", crewId, userId });
              return null;
            }),
        ]);

        setComplianceData(complianceResponseCached.data);
        setAircraftOverviewRows(aircraftOverviewCached.data.rows);
        setBoughtProducts(userBoughtCourses);
        setCatalogRequirements(catalog);
        setLoading(false);

        // Step 2: Load fresh compliance, then read the refreshed user aircraft-overview cache.
        await handleComplianceRefresh();
      } catch (err) {
        captureHandledException(err, { feature: "userCompliance", action: "load", crewId, userId });
        setError(t("compliance.load_failed"));
        setLoading(false);
      }
    })();
  }, [userId, crewId]);

  // Load Moodle products for All Courses tab
  const loadMoodleProducts = async () => {
    if (moodleProducts) return; // Already loaded

    setLoadingMoodleCourses(true);
    try {
      const productsData = await getAvailableProducts(crewId as any, userId as any);
      setMoodleProducts(productsData);
    } catch (err) {
      captureHandledException(err, { feature: "userCompliance", action: "loadMoodleProducts", crewId, userId });
      enqueueSnackbar(t("course.load_failed"), { variant: "error" });
    } finally {
      setLoadingMoodleCourses(false);
    }
  };

  // Load data when All Courses tab is clicked
  useEffect(() => {
    if (match.params.pageId === "all" && !moodleProducts && !loadingMoodleCourses) {
      loadMoodleProducts();
    }
  }, [match.params.pageId]);

  // Show history using certificateMap from compliance API
  const handleShowHistory = (certificate: any) => {
    setHistoryItem(certificate);
  };

  // Per-role findings (empty in catalog-fallback mode), split by the tab they
  // belong to: missing positions surface on the Positions tab, missing
  // operations on the Operations tab. The page-level info box never shows on
  // those two tabs when the catalog is available (their specific warnings — or
  // their absence — speak instead); it remains the summary signal on the other
  // tabs and, except on Operations, in fallback mode.
  const incompleteRoles =
    complianceData && catalogRequirements ? getIncompleteRoles(complianceData.relationTree, catalogRequirements) : [];
  const positionGaps = incompleteRoles.filter((entry) => entry.reason === "missing_positions");
  const operationGaps = incompleteRoles.filter((entry) => entry.reason === "missing_operations");
  const infoBoxSuppressed =
    match.params.pageId === "operations" || (match.params.pageId === "positions" && catalogRequirements !== null);
  // With a catalog the per-role walk above already answered the question;
  // without one, fall back to the legacy whole-profile heuristic.
  const profileSetupIncomplete = catalogRequirements
    ? incompleteRoles.length > 0
    : isProfileSetupIncomplete(complianceData?.relationTree, null);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>
          {showBackButton && <NavigationBack onBeforeGoBack={handleGoBack} />}{" "}
          {headerCaptions[match.params.pageId || ""] || t("compliance.title")}
        </h2>
        {userInfo && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <UserTitle id={`${userInfo.id}`} email={userInfo.email} />
            <IconButton
              size="small"
              color="primary"
              title={t("compliance.edit_user_properties")}
              onClick={goToUserProperties}
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          marginBottom: "1.5rem",
        }}
      >
        <UserNavigation
          match={match}
          history={history}
          location={location}
          profileSetupWarnings={{
            positions: positionGaps.length > 0,
            operations: operationGaps.length > 0,
          }}
        />
      </Box>

      {!loading && !error && complianceData && !infoBoxSuppressed && profileSetupIncomplete && (
        <Alert severity="info" sx={{ mb: "1.5rem" }}>
          {isSelfView ? (
            <>
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("compliance.action_required")}
              </Typography>
              <Typography variant="body2" component="span">
                {t("compliance.profile_setup_self_body")}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  fontWeight: 600,
                }}
              >
                {t("compliance.profile_setup_incomplete_title")}
              </Typography>
              <Typography variant="body2" component="span">
                {t("compliance.profile_setup_incomplete_body")}{" "}
              </Typography>
              <Link component="button" variant="body2" onClick={goToUserProperties}>
                {t("compliance.edit_user_properties")}
              </Link>
            </>
          )}
        </Alert>
      )}

      {error && (
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      )}

      {!loading && !error && complianceData && (
        <Box style={{ overflow: "auto" }}>
          <CertificateActions.Provider
            value={{
              showHistory: handleShowHistory,
              getBoughtCourses() {
                return boughtProducts;
              },
            }}
          >
            <CurrentUserCrewContext.Provider value={{ userId, crewId }}>
              {match.params.pageId === "positions" && (
                <Positions
                  complianceData={complianceData}
                  userId={userId}
                  crewId={crewId}
                  profileSetup={{
                    incompleteRoles: positionGaps,
                    isSelfView,
                    onEditProperties: goToUserProperties,
                  }}
                />
              )}
              {match.params.pageId === "operations" && (
                <Operations
                  complianceData={complianceData}
                  userId={userId}
                  crewId={crewId}
                  profileSetup={{
                    incompleteRoles: operationGaps,
                    isSelfView,
                    onEditProperties: goToUserProperties,
                  }}
                />
              )}
              {match.params.pageId === "aircraft" && (
                <Aircraft
                  complianceData={complianceData}
                  overviewRows={aircraftOverviewRows}
                  userId={userId}
                  crewId={crewId}
                  history={history}
                />
              )}
              {match.params.pageId === "all" && (
                <AllCourses
                  complianceData={complianceData}
                  allCourses={moodleProducts}
                  boughtProducts={boughtProducts}
                  loading={loadingMoodleCourses}
                />
              )}
              {match.params.pageId === "bundles" && (
                <Bundles crewId={crewId} userId={userId} boughtProducts={boughtProducts} />
              )}
            </CurrentUserCrewContext.Provider>
          </CertificateActions.Provider>
        </Box>
      )}

      {historyItem && complianceData && (
        <CourseHistory
          userId={userId}
          certificate={historyItem}
          certificateMap={complianceData.certificateMap}
          onClose={() => setHistoryItem(null)}
          onDeleteSuccess={handleComplianceRefresh}
          className=""
        />
      )}

      {showBackButton && (
        <Button
          title={t("navigate_back")}
          type="button"
          onClick={() => {
            handleGoBack();
            history.goBack();
          }}
        >
          {t("compliance.return_to_members")}
        </Button>
      )}
    </>
  );
};

export default UserCompliance;
